import React, { FocusEvent, FunctionComponent, ReactNode, useState } from "react";

import { TAB_INDEX_FOCUSABLE_TAB_SEQUENCE } from "@multiweb/reactUtils";
import { TabIndex } from "@multiweb/types/react";

export type FocusProviderPropsChildrenProps = {
    /** Příznak, zda je zaměřený element v rámci obalované oblasti */
    isFocused: boolean;
};

export type FocusProviderProps = {
    /** Umožňuje nastavit tabindex. Výchozí hodnota je TAB_INDEX_FOCUSABLE_TAB_SEQUENCE. */
    tabIndex?: TabIndex;
    /** Potomci, kteří mají k dispozici informaci o zaměření elementu v rámci obalované oblasti */
    children: (props: FocusProviderPropsChildrenProps) => ReactNode;
    /** Funkce volaná při ztrátě zaměření obalované oblasti */
    onBlur?: (event: FocusEvent) => void;
    /** Funkce volaná při zaměření v rámci obalované oblasti */
    onFocus?: (event: FocusEvent) => void;
};

/**
 * Komponenta slouží pro práci s informací o zaměření (`onFocus`) některého elementu v rámci
 * obalované oblasti. Informace (`isFocused`) je potomkům předávána technikou `render props`.
 */
export const FocusProvider: FunctionComponent<FocusProviderProps> = ({
    children,
    tabIndex = TAB_INDEX_FOCUSABLE_TAB_SEQUENCE,
    onBlur,
    onFocus,
}) => {
    const [isFocused, setIsFocused] = useState(false);

    const handleBlur = (event: FocusEvent) => {
        /* Pokud proběhl pokus o zaměření elementu mimo obalovanou oblast, tak se nastaví zaměření na `false`. */
        if (!event.relatedTarget || !event.currentTarget.contains(event.relatedTarget as Node)) {
            setIsFocused(false);
            onBlur && onBlur(event);
        }
    };

    const handleFocus = (event: FocusEvent) => {
        setIsFocused(true);
        onFocus && onFocus(event);
    };

    return (
        <div
            className="focus-provider-component"
            onFocus={handleFocus}
            onBlur={handleBlur}
            {...{ tabIndex }}
        >
            {children({ isFocused })}
        </div>
    );
};
